import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Phone, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchEpiscopalStaff } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface Episcopal {
  name: string;
  slug: string;
  role: string;
  email?: string;
  phone_number?: string;
  photo?: string; // optional photo
  brief_info?: string;
  role_description?: string;
  status?: string;
  hierarchy_level?: number;
}

const Modal = ({
  isOpen,
  onClose,
  staff,
}: {
  isOpen: boolean;
  onClose: () => void;
  staff: Episcopal | null;
}) => {
  if (!isOpen || !staff) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-11/12 sm:w-10/12 md:w-8/12 lg:w-6/12 max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          <X size={20} />
        </button>
        <h2 className="text-2xl font-bold mb-4 text-center">{staff.name}</h2>
        <p className="text-gray-700 mb-2 text-center">
          <span className="font-semibold">Role:</span> {staff.role}
        </p>
        {staff.brief_info && (
          <p className="text-gray-600 mb-2">
            <span className="font-semibold">Brief Info:</span> {staff.brief_info}
          </p>
        )}
        {staff.role_description && (
          <p className="text-gray-600">
            <span className="font-semibold">Role Description:</span> {staff.role_description}
          </p>
        )}
      </div>
    </div>
  );
};


const StaffCard = ({ staff, onClick }: { staff: Episcopal; onClick: () => void }) => (
  <Card
    className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
    onClick={onClick}
  >
    <CardHeader className="text-center pb-4">
      <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden">
        {staff.photo ? (
          <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            <span className="text-xl text-white">👤</span>
          </div>
        )}
      </div>
      <CardTitle className="text-lg text-gray-900">{staff.name}</CardTitle>
      <p className="text-blue-600 font-semibold">{staff.role}</p>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="space-y-2 text-sm">
        {staff.email && (
          <div className="flex items-center">
            <Mail className="h-4 w-4 text-gray-500 mr-2" />
            <Link to={`mailto:${staff.email}`} className="text-blue-600 hover:underline">
              {staff.email}
            </Link>
          </div>
        )}
        {staff.phone_number && (
          <div className="flex items-center">
            <Phone className="h-4 w-4 text-gray-500 mr-2" />
            <Link to={`tel:${staff.phone_number}`} className="text-blue-600 hover:underline">
              {staff.phone_number}
            </Link>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const EpiscopalOffice = () => {
  const [loading, setLoading] = useState(true);
  const [episcopalStaff, setEpiscopalStaff] = useState<Episcopal[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<Episcopal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetchEpiscopalStaff();
        const data = response.data || [];
        setEpiscopalStaff(
          data.sort((a: Episcopal, b: Episcopal) => (a.hierarchy_level || 0) - (b.hierarchy_level || 0))
        );
      } catch (error) {
        console.error('Error fetching Episcopal Staff:', error);

        // Fallback demo data
        const fallbackData: Episcopal[] = [
          {
            name: "Rev. Canon John Adebayo",
            slug: "rev-canon-john-adebayo",
            role: "Bishop's Chaplain",
            email: "chaplain@ibadasouthanglicandiocese.org.ng",
            phone_number: "+234 803 xxx xxxx",
            brief_info: "Bishop's Chaplain",
            role_description: "Supports the Bishop in administrative and pastoral duties.",
            status: "active",
            hierarchy_level: 1
          },
          {
            name: "Mrs. Grace Adeyemi",
            slug: "mrs-grace-adeyemi",
            role: "Executive Secretary",
            email: "secretary@ibadasouthanglicandiocese.org.ng",
            phone_number: "+234 806 xxx xxxx",
            brief_info: "Executive Secretary",
            role_description: "Handles administrative coordination and office management.",
            status: "active",
            hierarchy_level: 2
          },
          {
            name: "Mr. Samuel Ogundimu",
            slug: "mr-samuel-ogundimu",
            role: "Administrative Officer",
            email: "admin@ibadasouthanglicandiocese.org.ng",
            phone_number: "+234 807 xxx xxxx",
            brief_info: "Administrative Officer",
            role_description: "Manages diocesan records and supports office operations.",
            status: "active",
            hierarchy_level: 3
          },
          {
            name: "Mrs. Mary Oluwaseun",
            slug: "mrs-mary-oluwaseun",
            role: "Accounts Officer",
            email: "accounts@ibadasouthanglicandiocese.org.ng",
            phone_number: "+234 805 xxx xxxx",
            brief_info: "Accounts Officer",
            role_description: "Oversees financial records and transactions.",
            status: "active",
            hierarchy_level: 4
          }
        ];

        setEpiscopalStaff(fallbackData);

        toast({
          title: 'Note',
          description: 'Using sample data. API connection will be established for live data.',
          variant: 'default',
        });
      } finally {
        setLoading(false);
      }
    };

    loadStaff();
  }, [toast]);

  const handleCardClick = (staff: Episcopal) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Episcopal Office staff...</p>
        </div>
      </div>
    );
  }

  if (!episcopalStaff.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">No Episcopal Office staff available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Episcopal Office</h1>
          <p className="text-xl text-gray-600">The Administrative Heart of the Diocese</p>
        </div>

        {/* Bishop and Wife Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <Card className="border-none shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-4xl text-white">👨‍💼</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Rt. Rev. AkinTunde Popoola</h3>
              <p className="text-purple-600 font-semibold mb-4">Lord Bishop</p>
              <p className="text-gray-700 leading-relaxed">
                "I am committed to leading this diocese with wisdom, compassion, and dedication
                to the Gospel of Jesus Christ. Together, we shall build God's kingdom in Ibadan South."
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-4xl text-white">👩‍💼</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Dr. Oyebola Popoola</h3>
              <p className="text-pink-600 font-semibold mb-4">Bishop's Wife</p>
              <p className="text-gray-700 leading-relaxed">
                "Supporting the ministry through women's programs, community outreach,
                and pastoral care for families across our diocese."
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Episcopal Office Staff */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Workers in the Episcopal Office</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {episcopalStaff.map((staff, index) => (
              <StaffCard key={index} staff={staff} onClick={() => handleCardClick(staff)} />
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} staff={selectedStaff} />
    </div>
  );
};

export default EpiscopalOffice;
